import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Users, Car, ShoppingCart, PieChart, Landmark, Search, UserCircle2, X, Info } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, formatPercent, todayISO, FUEL_TYPES, ENTRY_TYPES, PAYMENT_METHODS } from '../lib/format';
import PageHeader from '../components/PageHeader';
import CurrencyInput from '../components/CurrencyInput';
import PhoneInput from '../components/PhoneInput';
import DocumentInput from '../components/DocumentInput';
import NumberInput from '../components/NumberInput';
import PersonPickerDialog from '../components/PersonPickerDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const CURRENT_YEAR = new Date().getFullYear();

export default function VehicleNew() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);
  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(isEditing);
  const [tab, setTab] = useState('veiculo');
  const [showQuickPartner, setShowQuickPartner] = useState(false);
  const [quickPartner, setQuickPartner] = useState({ name: '', email: '', phone: '' });
  const [showQuickAccount, setShowQuickAccount] = useState(false);
  const [showSellerPicker, setShowSellerPicker] = useState(false);
  const [quickAccount, setQuickAccount] = useState({ name: '', bank: '', agency: '', account_number: '' });

  const [form, setForm] = useState({
    // Veículo
    brand: '',
    model: '',
    version: '',
    year: CURRENT_YEAR,
    year_fabrication: '',
    plate: '',
    chassis: '',
    renavam: '',
    color: '',
    km: '',
    fuel_type: '',
    notes: '',
    // Compra
    purchase_date: todayISO(),
    entry_type: 'DIRECT',
    seller_name: '',
    seller_document: '',
    seller_phone: '',
    seller_email: '',
    seller_person_id: '',
    announced_price: '',
    purchase_price: '',
    minimum_sale_price: '',
    payment_method: '',
    payment_date: '',
    account_id: '',
    negotiation_notes: '',
    icms_credit_entry: '',
    aporte_from_balance: false,
    is_direct_passthrough: false,
  });

  useEffect(() => {
    api.get('/partners').then((r) => setPartners(r.data));
    api.get('/accounts').then((r) => setAccounts(r.data));
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    setLoadingVehicle(true);
    api.get(`/vehicles/${editId}`)
      .then(({ data }) => {
        setForm({
          brand: data.brand || '',
          model: data.model || '',
          version: data.version || '',
          year: data.year || CURRENT_YEAR,
          year_fabrication: data.year_fabrication || '',
          plate: data.plate || '',
          chassis: data.chassis || '',
          renavam: data.renavam || '',
          color: data.color || '',
          km: data.km || '',
          fuel_type: data.fuel_type || '',
          notes: data.notes || '',
          purchase_date: data.purchase_date || todayISO(),
          entry_type: data.entry_type || 'DIRECT',
          seller_name: data.seller_name || '',
          seller_document: data.seller_document || '',
          seller_phone: data.seller_phone || '',
          seller_email: data.seller_email || '',
          seller_person_id: data.seller_person_id || '',
          announced_price: data.announced_price ?? '',
          purchase_price: data.purchase_price ?? '',
          minimum_sale_price: data.minimum_sale_price ?? '',
          payment_method: data.payment_method || '',
          payment_date: data.payment_date || '',
          account_id: data.account_id || '',
          negotiation_notes: data.negotiation_notes || '',
          icms_credit_entry: data.icms_credit_entry ?? '',
          aporte_from_balance: Boolean(data.aporte_from_balance),
          is_direct_passthrough: Boolean(data.is_direct_passthrough),
        });
        setAllocations((data.allocations || []).map((a) => ({
          partner_id: a.partner_id,
          contribution_pct: a.contribution_pct,
          profit_pct: a.profit_pct,
        })));
      })
      .catch(() => {
        toast.error('Veículo não encontrado');
        navigate('/veiculos');
      })
      .finally(() => setLoadingVehicle(false));
  }, [isEditing, editId, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addAllocation = () => {
    const remaining = partners.filter((p) => !allocations.find((a) => a.partner_id === p.id));
    if (remaining.length === 0) {
      toast.warning('Nenhum sócio disponível. Cadastre um novo sócio.');
      return;
    }
    const next = remaining[0];
    setAllocations((a) => [...a, {
      partner_id: next.id,
      contribution_pct: next.default_contribution_pct ?? 0,
      profit_pct: next.default_profit_pct ?? 0,
    }]);
  };

  const updateAlloc = (idx, field, value) => {
    if (field === 'partner_id') {
      // When changing partner, also prefill defaults
      const newPartner = partners.find((p) => p.id === value);
      setAllocations((a) => a.map((al, i) => (i === idx ? {
        ...al,
        partner_id: value,
        contribution_pct: newPartner?.default_contribution_pct ?? al.contribution_pct,
        profit_pct: newPartner?.default_profit_pct ?? al.profit_pct,
      } : al)));
    } else {
      setAllocations((a) => a.map((al, i) => (i === idx ? { ...al, [field]: value } : al)));
    }
  };

  const removeAlloc = (idx) => setAllocations((a) => a.filter((_, i) => i !== idx));

  const sumContrib = allocations.reduce((s, a) => s + Number(a.contribution_pct || 0), 0);
  const sumProfit = allocations.reduce((s, a) => s + Number(a.profit_pct || 0), 0);
  const contribValid = Math.abs(sumContrib - 100) < 0.01;
  const profitValid = Math.abs(sumProfit - 100) < 0.01;

  const createQuickPartner = async () => {
    if (!quickPartner.name.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const { data } = await api.post('/partners', {
        name: quickPartner.name.trim(),
        email: quickPartner.email.trim() || null,
        phone: quickPartner.phone || null,
      });
      setPartners((p) => [data, ...p]);
      setShowQuickPartner(false);
      setQuickPartner({ name: '', email: '', phone: '' });
      setAllocations((a) => [...a, { partner_id: data.id, contribution_pct: 0, profit_pct: 0 }]);
      toast.success('Sócio criado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar sócio');
    }
  };

  const createQuickAccount = async () => {
    if (!quickAccount.name.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const { data } = await api.post('/accounts', {
        name: quickAccount.name.trim(),
        bank: quickAccount.bank.trim() || null,
        agency: quickAccount.agency.trim() || null,
        account_number: quickAccount.account_number.trim() || null,
      });
      setAccounts((a) => [data, ...a]);
      setShowQuickAccount(false);
      setQuickAccount({ name: '', bank: '', agency: '', account_number: '' });
      update('account_id', data.id);
      toast.success('Conta cadastrada');
    } catch (err) {
      toast.error('Erro ao criar conta');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.purchase_price) {
      toast.error('Marca, modelo e valor negociado são obrigatórios');
      setTab(!form.brand || !form.model ? 'veiculo' : 'compra');
      return;
    }
    if (allocations.length === 0) {
      toast.error('Adicione pelo menos um sócio na composição');
      setTab('composicao');
      return;
    }
    if (!contribValid || !profitValid) {
      toast.error('Percentuais devem somar 100%');
      setTab('composicao');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        year_fabrication: form.year_fabrication ? Number(form.year_fabrication) : null,
        km: form.km ? Number(form.km) : null,
        purchase_price: Number(form.purchase_price),
        announced_price: form.announced_price !== '' && form.announced_price !== null ? Number(form.announced_price) : null,
        minimum_sale_price: form.minimum_sale_price !== '' && form.minimum_sale_price !== null ? Number(form.minimum_sale_price) : null,
        fuel_type: form.fuel_type || null,
        entry_type: form.entry_type || null,
        payment_method: form.entry_type === 'TRADE' ? null : (form.payment_method || null),
        payment_date: form.entry_type === 'TRADE' ? null : (form.payment_date || null),
        account_id: form.entry_type === 'TRADE' ? null : (form.account_id || null),
        seller_name: form.seller_name?.trim() || null,
        seller_document: form.seller_document || null,
        seller_phone: form.seller_phone || null,
        seller_email: form.seller_email?.trim() || null,
        seller_person_id: form.seller_person_id || null,
        negotiation_notes: form.negotiation_notes?.trim() || null,
        icms_credit_entry: form.icms_credit_entry !== '' && form.icms_credit_entry !== null ? Number(form.icms_credit_entry) : null,
        aporte_from_balance: Boolean(form.aporte_from_balance),
        is_direct_passthrough: Boolean(form.is_direct_passthrough),
        allocations: allocations.map((a) => ({
          partner_id: a.partner_id,
          contribution_pct: Number(a.contribution_pct),
          profit_pct: Number(a.profit_pct),
        })),
      };
      let vehicleId;
      if (isEditing) {
        const { data } = await api.put(`/vehicles/${editId}`, payload);
        vehicleId = data.id;
        toast.success('Veículo atualizado!');
      } else {
        payload.status = 'PURCHASED';
        const { data } = await api.post('/vehicles', payload);
        vehicleId = data.id;
        toast.success('Veículo cadastrado!');
      }
      navigate(`/veiculos/${vehicleId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2" data-testid="vehicle-new-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
      </div>
      <PageHeader
        title={isEditing ? 'Editar veículo' : 'Novo veículo'}
        subtitle={isEditing ? 'Atualize os dados do veículo, da compra e da composição societária.' : 'Cadastre o veículo, os dados da compra e a composição societária.'}
      />

      {loadingVehicle ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-96" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : (
      <form onSubmit={submit} data-testid="vehicle-form">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-xl h-auto">
            <TabsTrigger value="veiculo" data-testid="tab-vehicle" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
              <Car className="h-4 w-4 shrink-0" /> <span>Veículo</span>
            </TabsTrigger>
            <TabsTrigger value="compra" data-testid="tab-purchase" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
              <ShoppingCart className="h-4 w-4 shrink-0" /> <span>Compra</span>
            </TabsTrigger>
            <TabsTrigger value="composicao" data-testid="tab-composition" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
              <PieChart className="h-4 w-4 shrink-0" /> <span>Composição</span>
            </TabsTrigger>
          </TabsList>

          {/* ============ TAB VEÍCULO ============ */}
          <TabsContent value="veiculo" className="mt-4">
            <Card className="border-border/70">
              <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Marca *</Label>
                  <Input value={form.brand} onChange={(e) => update('brand', e.target.value)} required data-testid="vehicle-brand-input" />
                </div>
                <div>
                  <Label>Modelo *</Label>
                  <Input value={form.model} onChange={(e) => update('model', e.target.value)} required data-testid="vehicle-model-input" />
                </div>
                <div>
                  <Label>Versão</Label>
                  <Input value={form.version} onChange={(e) => update('version', e.target.value)} placeholder="Ex: XLT, Touring, GTI" data-testid="vehicle-version-input" />
                </div>
                <div>
                  <Label>Ano de fabricação</Label>
                  <Input type="number" value={form.year_fabrication} onChange={(e) => update('year_fabrication', e.target.value)} placeholder={CURRENT_YEAR - 1} data-testid="vehicle-year-fab-input" />
                </div>
                <div>
                  <Label>Ano modelo *</Label>
                  <Input type="number" value={form.year} onChange={(e) => update('year', e.target.value)} required data-testid="vehicle-year-input" />
                </div>
                <div>
                  <Label>Combustível</Label>
                  <Select value={form.fuel_type} onValueChange={(v) => update('fuel_type', v)}>
                    <SelectTrigger data-testid="vehicle-fuel-input"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FUEL_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cor</Label>
                  <Input value={form.color} onChange={(e) => update('color', e.target.value)} />
                </div>
                <div>
                  <Label>KM</Label>
                  <NumberInput value={form.km} onChange={(v) => update('km', v)} placeholder="0" data-testid="vehicle-km-input" />
                </div>
                <div>
                  <Label>Placa</Label>
                  <Input value={form.plate} onChange={(e) => update('plate', e.target.value.toUpperCase())} placeholder="ABC1D23" data-testid="vehicle-plate-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Chassi</Label>
                  <Input value={form.chassis} onChange={(e) => update('chassis', e.target.value)} placeholder="17 caracteres" />
                </div>
                <div>
                  <Label>Renavam</Label>
                  <Input value={form.renavam} onChange={(e) => update('renavam', e.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="00000000000" data-testid="vehicle-renavam-input" />
                </div>
                <div className="md:col-span-3">
                  <Label>Observações do veículo</Label>
                  <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={2} placeholder="Ex: Carro de garagem, 2º dono, com manual e nota..." />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end mt-4">
              <Button type="button" onClick={() => setTab('compra')} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicle-next-purchase">
                Continuar para Compra →
              </Button>
            </div>
          </TabsContent>

          {/* ============ TAB COMPRA ============ */}
          <TabsContent value="compra" className="mt-4 space-y-4">
            <Card className="border-border/70">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-1">Negociação</h3>
                  <p className="text-xs text-muted-foreground">Detalhes da entrada do veículo na operação.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Data da negociação</Label>
                    <Input type="date" value={form.purchase_date} onChange={(e) => update('purchase_date', e.target.value)} data-testid="vehicle-purchase-date-input" />
                  </div>
                  <div>
                    <Label>Tipo de entrada</Label>
                    <Select value={form.entry_type} onValueChange={(v) => update('entry_type', v)}>
                      <SelectTrigger data-testid="vehicle-entry-type-input"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ENTRY_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1" />
                  <div>
                    <Label>Valor anunciado pelo vendedor</Label>
                    <CurrencyInput value={form.announced_price} onChange={(v) => update('announced_price', v)} data-testid="vehicle-announced-price-input" />
                    <p className="text-[11px] text-muted-foreground mt-1">Valor pedido originalmente.</p>
                  </div>
                  <div>
                    <Label>Valor negociado *</Label>
                    <CurrencyInput value={form.purchase_price} onChange={(v) => update('purchase_price', v)} required data-testid="vehicle-purchase-price-input" />
                    <p className="text-[11px] text-muted-foreground mt-1">Valor final pago. Usado no cálculo do resultado.</p>
                  </div>
                  <div>
                    <Label>Valor mínimo de venda</Label>
                    <CurrencyInput value={form.minimum_sale_price} onChange={(v) => update('minimum_sale_price', v)} data-testid="vehicle-minimum-sale-price-input" />
                    <p className="text-[11px] text-muted-foreground mt-1">Usado para projeções enquanto não vendido.</p>
                  </div>
                </div>

                <Separator />
                <label className="flex items-start gap-3 p-3 rounded-lg border border-border/70 hover:bg-muted/30 cursor-pointer" data-testid="passthrough-toggle">
                  <input
                    type="checkbox"
                    checked={form.is_direct_passthrough}
                    onChange={(e) => update('is_direct_passthrough', e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      Passagem direta — sem transferência para a empresa
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              tabIndex={0}
                              role="button"
                              aria-label="O que muda com Passagem direta"
                              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-muted-foreground/10 text-muted-foreground hover:bg-muted-foreground/20 focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                              style={{ cursor: 'default' }}
                              onClick={(e) => e.preventDefault()}
                            >
                              <Info className="h-2.5 w-2.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-sm text-xs">
                            <div className="font-semibold mb-1.5">O que muda ao marcar:</div>
                            <ul className="space-y-1 list-disc list-inside text-xs">
                              <li><strong>Sem impostos</strong> — PIS/COFINS/IRPJ/CSLL/ICMS = 0 (CRV não passa pela empresa)</li>
                              <li><strong>DRE sem bloco de impostos</strong></li>
                              <li>Crédito de ICMS de entrada fica oculto (não há tributação a abater)</li>
                            </ul>
                            <div className="font-semibold mt-2 mb-1.5">O que continua igual:</div>
                            <ul className="space-y-1 list-disc list-inside text-xs">
                              <li>Conta como <strong>capital em estoque</strong> (o carro fica no pátio)</li>
                              <li>Tem forma de pagamento, conta bancária e despesas operacionais</li>
                              <li>Stepper com todas as etapas (Comprado → Em preparação → Disponível → Vendido)</li>
                              <li>Composição societária padrão dos sócios</li>
                              <li>Margem credita normalmente nas contas-correntes</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Marque quando o carro entra no estoque mas o CRV vai direto do vendedor original para o comprador final — sem transferir para o nome da empresa. <strong>Resultado: zero impostos federais/estaduais.</strong>
                    </div>
                  </div>
                </label>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="text-sm font-semibold mb-1">Vendedor</h3>
                    <p className="text-xs text-muted-foreground">Pessoa física ou jurídica de quem o veículo foi comprado.</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSellerPicker(true)}
                    data-testid="vehicle-seller-picker-trigger"
                  >
                    <Search className="h-3.5 w-3.5 mr-1.5" /> Selecionar cadastrado
                  </Button>
                </div>

                {form.seller_person_id && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-center gap-2 text-sm">
                    <UserCircle2 className="h-4 w-4 text-blue-700 shrink-0" />
                    <span className="text-blue-900 flex-1 min-w-0 truncate">
                      Vinculado ao cadastro de <strong>{form.seller_name || 'pessoa'}</strong> · os campos abaixo são editáveis sem alterar o cadastro
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => update('seller_person_id', '')}
                      title="Desvincular do cadastro"
                      data-testid="vehicle-seller-clear-link"
                    >
                      <X className="h-3.5 w-3.5 text-blue-700" />
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome / Razão social</Label>
                    <Input value={form.seller_name} onChange={(e) => update('seller_name', e.target.value)} data-testid="vehicle-seller-name-input" />
                  </div>
                  <div>
                    <Label>CPF / CNPJ</Label>
                    <DocumentInput value={form.seller_document} onChange={(v) => update('seller_document', v)} data-testid="vehicle-seller-document-input" />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <PhoneInput value={form.seller_phone} onChange={(v) => update('seller_phone', v)} data-testid="vehicle-seller-phone-input" />
                  </div>
                  <div>
                    <Label>E-mail</Label>
                    <Input type="email" value={form.seller_email} onChange={(e) => update('seller_email', e.target.value)} data-testid="vehicle-seller-email-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {form.entry_type !== 'TRADE' ? (
              <Card className="border-border/70">
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold mb-1">Pagamento</h3>
                    <p className="text-xs text-muted-foreground">Forma, data e conta de origem do pagamento.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Forma de pagamento</Label>
                      <Select value={form.payment_method} onValueChange={(v) => update('payment_method', v)}>
                        <SelectTrigger data-testid="vehicle-payment-method-input"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PAYMENT_METHODS).filter(([k]) => k !== 'TRADE').map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Data do pagamento</Label>
                      <Input type="date" value={form.payment_date} onChange={(e) => update('payment_date', e.target.value)} data-testid="vehicle-payment-date-input" />
                    </div>
                    <div>
                      <Label>Conta de origem</Label>
                      <div className="flex gap-2">
                        <Select value={form.account_id} onValueChange={(v) => update('account_id', v)}>
                          <SelectTrigger className="flex-1" data-testid="vehicle-account-input"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            {accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.name}{a.bank ? ` · ${a.bank}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="icon" onClick={() => setShowQuickAccount(true)} data-testid="vehicle-add-account">
                          <Landmark className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Observações da negociação</Label>
                    <Textarea
                      value={form.negotiation_notes}
                      onChange={(e) => update('negotiation_notes', e.target.value)}
                      rows={2}
                      placeholder='Ex: "aceitou desconto por pneus ruins"'
                      data-testid="vehicle-negotiation-notes-input"
                    />
                  </div>
                  <div>
                    <Label>Crédito de ICMS na entrada (opcional)</Label>
                    <CurrencyInput
                      value={form.icms_credit_entry}
                      onChange={(v) => update('icms_credit_entry', v)}
                      disabled={form.is_direct_passthrough}
                      data-testid="vehicle-icms-credit-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {form.is_direct_passthrough
                        ? 'Não aplicável em passagem direta (sem tributação).'
                        : 'Preencha se o veículo foi comprado de PJ com ICMS destacado em NF. Será abatido no cálculo de ICMS a recolher na venda.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border/70 bg-amber-50/40 border-amber-200">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-amber-200 grid place-items-center shrink-0">
                      <Landmark className="h-4 w-4 text-amber-800" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-amber-900 mb-1">Sem pagamento financeiro</h3>
                      <p className="text-xs text-amber-800">
                        Para tipo de entrada <strong>Troca</strong>, o veículo entrou no estoque como pagamento de outra venda — não há forma de pagamento, data nem conta envolvida.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>Observações da negociação</Label>
                    <Textarea
                      value={form.negotiation_notes}
                      onChange={(e) => update('negotiation_notes', e.target.value)}
                      rows={2}
                      placeholder='Ex: "veículo recebido em troca, com avaliação de R$ 30.000"'
                      data-testid="vehicle-negotiation-notes-input"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between mt-4">
              <Button type="button" variant="outline" onClick={() => setTab('veiculo')}>← Voltar</Button>
              <Button type="button" onClick={() => setTab('composicao')} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicle-next-composition">
                Continuar para Composição →
              </Button>
            </div>
          </TabsContent>

          {/* ============ TAB COMPOSIÇÃO ============ */}
          <TabsContent value="composicao" className="mt-4">
            <Card className="border-border/70">
              <CardContent className="p-6 space-y-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold mb-1">Composição societária</h3>
                    <p className="text-xs text-muted-foreground">Defina o % de aporte (capital) e o % de resultado (distribuição do lucro) de cada sócio.</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowQuickPartner(true)} data-testid="vehicle-add-quick-partner">
                      <Users className="h-4 w-4 mr-1" /> Novo sócio
                    </Button>
                    <Button type="button" size="sm" onClick={addAllocation} disabled={partners.length === 0} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicle-add-allocation">
                      <Plus className="h-4 w-4 mr-1" /> Adicionar
                    </Button>
                  </div>
                </div>

                {allocations.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                    Nenhum sócio adicionado. Clique em "Adicionar" para incluir sócios na composição.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allocations.map((alloc, idx) => {
                      const availablePartners = partners.filter(
                        (p) => p.id === alloc.partner_id || !allocations.find((a) => a.partner_id === p.id),
                      );
                      const aporteValue = form.purchase_price ? (Number(form.purchase_price) * Number(alloc.contribution_pct)) / 100 : 0;
                      return (
                        <div key={idx} className="border border-border/70 bg-muted/20 rounded-xl p-3" data-testid="partner-allocation-row">
                          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_120px_120px_40px] gap-3 items-start">
                            <div className="min-w-0">
                              <Label className="text-xs">Sócio</Label>
                              <Select value={alloc.partner_id} onValueChange={(v) => updateAlloc(idx, 'partner_id', v)}>
                                <SelectTrigger className="truncate" data-testid="partner-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {availablePartners.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="text-[11px] text-muted-foreground mt-1 tabular">
                                Aporte: {formatBRL(aporteValue)}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">% Aporte</Label>
                              <Input
                                type="number" step="0.01" min="0" max="100"
                                className="text-right tabular"
                                value={alloc.contribution_pct}
                                onChange={(e) => updateAlloc(idx, 'contribution_pct', e.target.value === '' ? 0 : Number(e.target.value))}
                                data-testid="partner-capital-percent-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">% Resultado</Label>
                              <Input
                                type="number" step="0.01" min="0" max="100"
                                className="text-right tabular"
                                value={alloc.profit_pct}
                                onChange={(e) => updateAlloc(idx, 'profit_pct', e.target.value === '' ? 0 : Number(e.target.value))}
                                data-testid="partner-profit-percent-input"
                              />
                            </div>
                            <div className="pt-6">
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeAlloc(idx)} data-testid="partner-allocation-remove">
                                <Trash2 className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {allocations.length > 0 && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className={`rounded-lg p-3 ${contribValid ? 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]' : 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]'}`}>
                        <div className="text-xs">Soma % Aporte</div>
                        <div className="font-semibold tabular" data-testid="sum-contribution">{formatPercent(sumContrib)}</div>
                      </div>
                      <div className={`rounded-lg p-3 ${profitValid ? 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]' : 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]'}`}>
                        <div className="text-xs">Soma % Resultado</div>
                        <div className="font-semibold tabular" data-testid="sum-profit">{formatPercent(sumProfit)}</div>
                      </div>
                    </div>

                    <Separator />
                    <label className="flex items-start gap-3 p-3 rounded-lg border border-border/70 hover:bg-muted/30 cursor-pointer" data-testid="aporte-from-balance-toggle">
                      <input
                        type="checkbox"
                        checked={form.aporte_from_balance}
                        onChange={(e) => update('aporte_from_balance', e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">Aporte deste veículo veio do saldo dos sócios?</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Marque se o capital de compra saiu do saldo da conta-corrente de cada sócio (será debitado automaticamente). Desmarque se o dinheiro veio de fora (não afeta os saldos).
                        </div>
                      </div>
                    </label>
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between mt-4 gap-2">
              <Button type="button" variant="outline" onClick={() => setTab('compra')}>← Voltar</Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="vehicle-form-cancel">Cancelar</Button>
                <Button type="submit" disabled={submitting} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicle-form-submit">
                  {submitting ? 'Salvando...' : (isEditing ? 'Atualizar veículo' : 'Salvar veículo')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </form>
      )}

      {/* Quick partner */}
      <Dialog open={showQuickPartner} onOpenChange={setShowQuickPartner}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo sócio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={quickPartner.name} onChange={(e) => setQuickPartner((q) => ({ ...q, name: e.target.value }))} data-testid="quick-partner-name" />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={quickPartner.email} onChange={(e) => setQuickPartner((q) => ({ ...q, email: e.target.value }))} />
            </div>
            <div>
              <Label>Telefone</Label>
              <PhoneInput value={quickPartner.phone} onChange={(v) => setQuickPartner((q) => ({ ...q, phone: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickPartner(false)}>Cancelar</Button>
            <Button onClick={createQuickPartner} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="quick-partner-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PersonPickerDialog
        open={showSellerPicker}
        onOpenChange={setShowSellerPicker}
        selectedId={form.seller_person_id}
        onSelect={(p) => {
          setForm((f) => ({
            ...f,
            seller_person_id: p.id,
            seller_name: p.name || '',
            seller_document: p.document || '',
            seller_phone: p.phone || '',
            seller_email: p.email || '',
          }));
        }}
      />

      {/* Quick account */}
      <Dialog open={showQuickAccount} onOpenChange={setShowQuickAccount}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova conta bancária</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Apelido / Nome da conta *</Label>
              <Input value={quickAccount.name} onChange={(e) => setQuickAccount((q) => ({ ...q, name: e.target.value }))} placeholder="Ex: Itaú PJ Principal" data-testid="quick-account-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input value={quickAccount.bank} onChange={(e) => setQuickAccount((q) => ({ ...q, bank: e.target.value }))} placeholder="Itaú, Nubank..." />
              </div>
              <div>
                <Label>Agência</Label>
                <Input value={quickAccount.agency} onChange={(e) => setQuickAccount((q) => ({ ...q, agency: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Conta</Label>
              <Input value={quickAccount.account_number} onChange={(e) => setQuickAccount((q) => ({ ...q, account_number: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickAccount(false)}>Cancelar</Button>
            <Button onClick={createQuickAccount} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="quick-account-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
